import { useEffect, useMemo } from 'react';
import { useReduxState } from 'hooks';

import FilterButtonItems from './FilterButtonItems';
import FilterButtonOpenInput from './FilterButtonOpenInput';
import FilterButtonRoot from './FilterButtonRoot';
import FilterButtonSingleItem from './FilterButtonSingleItem';
import FilterButtonSubMenu from './FilterButtonSubMenu';
import { buildFilterStateFromParamRecord } from './filterStateFromParams';
import { useFiltersContext, useFiltersDispatch } from './FilterContext';

import type { ButtonInterface } from 'components/common/Button';
import type { FilterItem } from '.';

interface Props {
  filterId: string;
  triggerProps?: ButtonInterface;
}

const FilterButton = ({ filterId, triggerProps }: Props) => {
  const { appliedParamsByFilterId, filters, state } = useFiltersContext();

  const filterDispatch = useFiltersDispatch();

  const { theme } = useReduxState();

  const appliedForId = appliedParamsByFilterId[filterId];

  useEffect(() => {
    const filterByField =
      filters?.[filterId]?.reduce(
        (items, item) => {
          items[item.field] = true;
          if (item.filters?.length) {
            item.filters.forEach((subFilter) => {
              items[subFilter.field] = subFilter;
            });
          }
          return items;
        },
        { presentInAll: {} }
      ) || {};

    const source = appliedForId || {};

    const initialState = buildFilterStateFromParamRecord(source, (key) =>
      Boolean(filterByField?.[key])
    );

    filterDispatch({ type: 'updateState', state: initialState, filterId });
  }, [appliedForId, filterId, filters]);

  const handleChange = (value: { [value: string]: true }, field: string) => {
    if (!value && !state?.[filterId]?.[field]) return;

    if (!value && state?.[filterId]?.[field]) handleClear(field);
    else {
      filterDispatch({
        type: 'updateState',
        filterId,
        state: { ...state?.[filterId], [field]: value }
      });
    }
  };

  const clearFields = (fields: string[]) => {
    const newState = { ...state?.[filterId] };
    if (newState.presentInAll) {
      newState.presentInAll = { ...newState.presentInAll };
    }
    for (const field of fields) {
      delete newState[field];
      if (newState.presentInAll?.[field]) {
        delete newState.presentInAll[field];
        if (Object.keys(newState.presentInAll).length === 0) {
          delete newState.presentInAll;
        }
      }
    }
    filterDispatch({ type: 'updateState', filterId, state: newState });
  };

  const handleClear = (field: string) => clearFields([field]);

  const filtersByField = useMemo(
    () =>
      filters?.[filterId]?.reduce((filters, filter) => {
        filters[filter.field] = filter;
        if (filter.filters?.length) {
          filter.filters.forEach((subFilter) => {
            filters[subFilter.field] = subFilter;
          });
        }
        return filters;
      }, {} as Record<string, FilterItem>) || {},
    [filters, filterId]
  );

  const numberOfActiveFilters = Object.keys(appliedForId || {}).filter(
    (key) => filtersByField?.[key] && appliedForId?.[key]
  ).length;

  return !filters?.[filterId]?.length ? null : (
    <FilterButtonRoot
      contentTopOffset={5}
      numberOfActiveFilters={numberOfActiveFilters}
      onClear={() => filterDispatch({ type: 'clearFilters', filterId })}
      onSubmit={() => filterDispatch({ type: 'submitFilters', filterId })}
      portalId={`filters-root-portal-${filterId}`}
      triggerProps={triggerProps}
    >
      {({ setRootIsOpen }) =>
        filters?.[filterId].map((item, i) => (
          <div className="filter" key={i}>
            {item.filters?.length ? (
              <FilterButtonSubMenu
                filterId={filterId}
                icon={item.icon || null}
                itemsActiveCount={
                  item.filters.filter(
                    (subFilter) => state?.[filterId]?.[subFilter.field]
                  ).length
                }
                parentPortalId={`filters-root-portal-${filterId}`}
                portalId={`${item.field}-${filterId}`}
                onClear={() => {
                  clearFields(item.filters.map((sub) => sub.field));
                }}
                setRootIsOpen={setRootIsOpen}
                title={item.title}
              >
                <div className="filters-inner">
                  {item.filters?.map((subFilter, j) => {
                    const currentFilterValue =
                      state?.[filterId]?.[subFilter.field] || {};

                    const activeItemsCount =
                      Object.keys(currentFilterValue).length || 0;

                    const filterStatusIsAllSelected =
                      activeItemsCount === 1 &&
                      subFilter.itemsAll?.value &&
                      currentFilterValue[subFilter.itemsAll.value];

                    return (
                      <div className="filter" key={j}>
                        <FilterButtonSubMenu
                          filterId={filterId}
                          icon={subFilter.icon || null}
                          itemsActiveCount={activeItemsCount}
                          itemsActiveCountCategory="dark"
                          parentPortalId={`filters-root-portal-${filterId}`}
                          portalId={subFilter.field}
                          onClear={() => handleClear(subFilter.field)}
                          setRootIsOpen={setRootIsOpen}
                          title={subFilter.title}
                          {...(subFilter.selectAllOnClick && subFilter.itemsAll
                            ? {
                                onSubMenuClick: () =>
                                  !activeItemsCount
                                    ? handleChange(
                                        { [subFilter.itemsAll.value]: true },
                                        subFilter.field
                                      )
                                    : filterStatusIsAllSelected
                                    ? handleClear(subFilter.field)
                                    : undefined
                              }
                            : {})}
                        >
                          {subFilter.items?.length ? (
                            <FilterButtonItems
                              filterId={filterId}
                              parentPortalId={`filters-root-portal-${filterId}`}
                              selectAllOnClick={item.selectAllOnClick}
                              setRootIsOpen={setRootIsOpen}
                              {...subFilter}
                            />
                          ) : (
                            <FilterButtonOpenInput
                              {...subFilter}
                              filterId={filterId}
                              initialValue={
                                Object.keys(
                                  state?.[filterId]?.[subFilter.field] || {}
                                )[0]
                              }
                              onInputChange={(value: string) =>
                                handleChange(
                                  value ? { [value]: true } : null,
                                  subFilter.field
                                )
                              }
                              setRootIsOpen={setRootIsOpen}
                            />
                          )}
                        </FilterButtonSubMenu>
                      </div>
                    );
                  })}
                </div>
              </FilterButtonSubMenu>
            ) : item.items?.length ? (
              <FilterButtonSubMenu
                filterId={filterId}
                icon={item.icon || null}
                itemsActiveCount={
                  Object.keys(state?.[filterId]?.[item.field] || {}).length
                }
                parentPortalId={`filters-root-portal-${filterId}`}
                portalId={`${item.field}-${filterId}`}
                onClear={() => handleClear(item.field)}
                setRootIsOpen={setRootIsOpen}
                title={item.title}
              >
                {item.input ? (
                  <FilterButtonOpenInput
                    {...item}
                    filterId={filterId}
                    initialValue={
                      Object.keys(state?.[filterId]?.[item.field] || {})[0]
                    }
                    onInputChange={(value: string) =>
                      handleChange(value ? { [value]: true } : null, item.field)
                    }
                    setRootIsOpen={setRootIsOpen}
                  />
                ) : (
                  <FilterButtonItems
                    filterId={filterId}
                    parentPortalId={`filters-root-portal-${filterId}`}
                    selectAllOnClick={item.selectAllOnClick}
                    setRootIsOpen={setRootIsOpen}
                    {...item}
                  />
                )}
              </FilterButtonSubMenu>
            ) : item.input ? (
              <FilterButtonSubMenu
                filterId={filterId}
                icon={item.icon || null}
                itemsActiveCount={0}
                parentPortalId={`filters-root-portal-${filterId}`}
                portalId={`${item.field}-${filterId}`}
                onClear={() => handleClear(item.field)}
                setRootIsOpen={setRootIsOpen}
                title={item.title}
              >
                <FilterButtonOpenInput
                  {...item}
                  filterId={filterId}
                  initialValue={
                    Object.keys(state?.[filterId]?.[item.field] || {})[0]
                  }
                  onInputChange={(value: string) =>
                    handleChange(value ? { [value]: true } : null, item.field)
                  }
                  setRootIsOpen={setRootIsOpen}
                />
              </FilterButtonSubMenu>
            ) : (
              <FilterButtonSingleItem
                checked={Boolean(state?.[filterId]?.[item.field])}
                icon={item.icon || null}
                label={item.title}
                onClick={() =>
                  handleChange(
                    state?.[filterId]?.[item.field] ? null : { true: true },
                    item.field
                  )
                }
              />
            )}

            <style jsx>{`
              .filters-inner {
                max-height: 300px;
                overflow: scroll;
              }
              .filter {
                min-width: 240px;
              }
              .filter:not(:first-child) {
                border-top: 1px solid ${theme.lightGrey};
              }
            `}</style>
          </div>
        ))
      }
    </FilterButtonRoot>
  );
};

export default FilterButton;
